export type SensitiveAttributeCountItem = {
  attribute: string;
  count: number;
};

export type SensitiveAttributeCountScope = 'attribute_mentions' | 'all_sources' | 'non_whitelisted';

export type SensitiveAttributeCountResult = {
  items: SensitiveAttributeCountItem[];
  totalMentions: number;
  scope: SensitiveAttributeCountScope;
};

const sumBySourceMaps = (bySource: Record<string, unknown>): Record<string, number> => {
  const totals: Record<string, number> = {};
  for (const source of ['prompt', 'metadata', 'output'] as const) {
    const map = bySource[source];
    if (!map || typeof map !== 'object' || Array.isArray(map)) continue;
    for (const [attr, count] of Object.entries(map as Record<string, unknown>)) {
      const n = Number(count);
      if (!Number.isNaN(n)) totals[attr] = (totals[attr] ?? 0) + n;
    }
  }
  return totals;
};

const itemsFromTotals = (totals: Record<string, number>): SensitiveAttributeCountItem[] =>
  Object.entries(totals)
    .map(([attribute, count]) => ({ attribute, count }))
    .filter(row => row.count > 0)
    .sort((a, b) => b.count - a.count);

/** Build per-attribute mention counts from aggregate_summary using one consistent source. */
export const buildSensitiveAttributeCounts = (
  aggregate: Record<string, unknown> | null | undefined
): SensitiveAttributeCountResult | null => {
  if (!aggregate || typeof aggregate !== 'object') return null;

  const mentions = aggregate.attribute_mentions;
  if (mentions && typeof mentions === 'object' && !Array.isArray(mentions) && Object.keys(mentions).length > 0) {
    const items = itemsFromTotals(
      Object.fromEntries(
        Object.entries(mentions as Record<string, unknown>)
          .map(([attribute, count]) => [attribute, Number(count)])
          .filter(([, count]) => !Number.isNaN(count))
      )
    );
    if (items.length === 0) return null;
    return {
      items,
      totalMentions: items.reduce((sum, row) => sum + row.count, 0),
      scope: 'attribute_mentions'
    };
  }

  const nonWhitelisted = aggregate.non_whitelisted_sensitive_by_source;
  if (nonWhitelisted && typeof nonWhitelisted === 'object' && !Array.isArray(nonWhitelisted)) {
    const items = itemsFromTotals(sumBySourceMaps(nonWhitelisted as Record<string, unknown>));
    if (items.length > 0) {
      return {
        items,
        totalMentions: items.reduce((sum, row) => sum + row.count, 0),
        scope: 'non_whitelisted'
      };
    }
  }

  const bySource = aggregate.sensitive_by_source;
  if (bySource && typeof bySource === 'object' && !Array.isArray(bySource)) {
    const items = itemsFromTotals(sumBySourceMaps(bySource as Record<string, unknown>));
    if (items.length > 0) {
      return {
        items,
        totalMentions: items.reduce((sum, row) => sum + row.count, 0),
        scope: 'all_sources'
      };
    }
  }

  return null;
};

export const sensitiveMentionScopeLabel = (scope: SensitiveAttributeCountScope): string => {
  switch (scope) {
    case 'non_whitelisted':
      return 'non-whitelisted mentions';
    case 'all_sources':
      return 'mentions (all sources)';
    default:
      return 'mentions';
  }
};
